import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api, { API, coverFor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { selectNextTrackIndex, shouldPrepareNextTrack } from "./playerQueue";

const PlayerCtx = createContext(null);
const PREVIEW_SEC = 30;
const PREVIEW_STREAM_VERSION = 2;
const PLAYBACK_RETRY_DELAYS = [1000, 2500, 5000];

function playAudio(audio, onFailure) {
  if (!audio) return;
  const result = audio.play();
  if (result?.catch) result.catch(onFailure);
}

export function PlayerProvider({ children }) {
  const { user, hasActiveLicense } = useAuth();
  const audioRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const shouldResumeRef = useRef(false);
  const queueRef = useRef([]);
  const indexRef = useRef(-1);
  const shuffleRef = useRef(false);
  const currentRef = useRef(null);
  const preparedNextRef = useRef(null);
  const preparingNextRef = useRef(null);
  const directTransitionRef = useRef(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(-1);
  const [reloadKey, setReloadKey] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [previewMode, setPreviewMode] = useState(true);
  const [shuffle, setShuffle] = useState(false);
  const [shuffleSource, setShuffleSource] = useState("");

  const current = index >= 0 ? queue[index] : null;
  const canFull = !!user && (["admin", "superadmin"].includes(user.role) || hasActiveLicense);
  queueRef.current = queue;
  indexRef.current = index;
  shuffleRef.current = shuffle;
  currentRef.current = current;

  const buildStreamUrl = useCallback(async (track) => {
    if (!track?.id) return "";
    if (!canFull) return `${API}/tracks/${track.id}/stream?mode=preview&v=${PREVIEW_STREAM_VERSION}`;
    const { data } = await api.post(`/tracks/${track.id}/playback-token`);
    return `${API}/tracks/${track.id}/stream?mode=full&playback_token=${encodeURIComponent(data.token)}`;
  }, [canFull]);

  const next = useCallback(() => {
    shouldResumeRef.current = true;
    preparedNextRef.current = null;
    preparingNextRef.current = null;
    directTransitionRef.current = null;
    setIndex((i) => {
      if (!queue.length) return -1;
      return selectNextTrackIndex(i, queue.length, shuffle);
    });
  }, [queue, shuffle]);

  const prev = useCallback(() => {
    shouldResumeRef.current = true;
    preparedNextRef.current = null;
    preparingNextRef.current = null;
    directTransitionRef.current = null;
    setIndex((i) => (queue.length ? (i - 1 + queue.length) % queue.length : -1));
  }, [queue]);

  const retryOrSkip = useCallback(() => {
    if (!shouldResumeRef.current || !queue.length || retryTimerRef.current) return;
    const retryIndex = retryCountRef.current;
    if (retryIndex < PLAYBACK_RETRY_DELAYS.length) {
      retryCountRef.current += 1;
      retryTimerRef.current = window.setTimeout(() => {
        retryTimerRef.current = null;
        setReloadKey((value) => value + 1);
      }, PLAYBACK_RETRY_DELAYS[retryIndex]);
      return;
    }
    retryCountRef.current = 0;
    next();
  }, [next, queue.length]);

  const prepareNextTrack = useCallback(() => {
    if (!canFull || !shouldResumeRef.current) return;
    const currentQueue = queueRef.current;
    const currentIndex = indexRef.current;
    const currentTrack = currentRef.current;
    if (!currentTrack || !currentQueue.length) return;

    const nextIndex = selectNextTrackIndex(
      currentIndex,
      currentQueue.length,
      shuffleRef.current,
    );
    const nextTrack = nextIndex >= 0 ? currentQueue[nextIndex] : null;
    if (!nextTrack?.id || nextTrack.id === currentTrack.id) return;

    const preparationKey = `${currentTrack.id}:${nextTrack.id}:${nextIndex}`;
    if (preparedNextRef.current?.key === preparationKey || preparingNextRef.current === preparationKey) return;

    preparingNextRef.current = preparationKey;
    buildStreamUrl(nextTrack)
      .then((url) => {
        const queueStillMatches = queueRef.current[nextIndex]?.id === nextTrack.id;
        if (url && currentRef.current?.id === currentTrack.id && queueStillMatches) {
          preparedNextRef.current = {
            key: preparationKey,
            fromTrackId: currentTrack.id,
            nextIndex,
            nextTrack,
            url,
          };
        }
      })
      .catch(() => {})
      .finally(() => {
        if (preparingNextRef.current === preparationKey) preparingNextRef.current = null;
      });
  }, [buildStreamUrl, canFull]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onTime = () => {
      setProgress(a.currentTime);
      if (shouldPrepareNextTrack(a.currentTime, a.duration, previewMode)) prepareNextTrack();
      if (previewMode && a.currentTime >= PREVIEW_SEC) {
        a.pause();
        a.currentTime = 0;
      }
      if ("mediaSession" in navigator && navigator.mediaSession.setPositionState) {
        try {
          if (Number.isFinite(a.duration) && a.duration > 0) {
            navigator.mediaSession.setPositionState({
              duration: a.duration,
              playbackRate: a.playbackRate || 1,
              position: Math.min(a.currentTime, a.duration),
            });
          }
        } catch {}
      }
    };
    const onDur = () => {
      setDuration(a.duration || 0);
      if (shouldPrepareNextTrack(a.currentTime, a.duration, previewMode)) prepareNextTrack();
    };
    const onEnd = () => {
      retryCountRef.current = 0;
      if (shouldResumeRef.current && queueRef.current.length === 1) {
        a.currentTime = 0;
        playAudio(a, retryOrSkip);
        return;
      }
      const prepared = preparedNextRef.current;
      const preparedIsCurrent = prepared?.fromTrackId === currentRef.current?.id;
      const queueStillMatches = prepared && queueRef.current[prepared.nextIndex]?.id === prepared.nextTrack.id;
      if (shouldResumeRef.current && preparedIsCurrent && queueStillMatches) {
        preparedNextRef.current = null;
        preparingNextRef.current = null;
        directTransitionRef.current = { trackId: prepared.nextTrack.id };
        indexRef.current = prepared.nextIndex;
        setIndex(prepared.nextIndex);
        setProgress(0);
        setDuration(prepared.nextTrack.duration || 0);
        a.src = prepared.url;
        a.load();
        playAudio(a, retryOrSkip);
        return;
      }
      next();
    };
    const onError = () => {
      setPlaying(false);
      retryOrSkip();
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onCanPlay = () => {
      retryCountRef.current = 0;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onDur);
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onError);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    a.addEventListener("canplay", onCanPlay);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onDur);
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("error", onError);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
      a.removeEventListener("canplay", onCanPlay);
    };
  }, [previewMode, next, prepareNextTrack, retryOrSkip]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted]);

  useEffect(() => {
    if (!current || !audioRef.current) return;
    let cancelled = false;
    setPreviewMode(!canFull);
    setProgress(0);
    setDuration(current.duration || 0);
    const loadCurrent = async () => {
      try {
        if (directTransitionRef.current?.trackId === current.id) {
          directTransitionRef.current = null;
          return;
        }
        const url = await buildStreamUrl(current);
        const audio = audioRef.current;
        if (cancelled || !audio || !url) return;
        audio.preload = "auto";
        audio.src = url;
        audio.load();
        if (shouldResumeRef.current) playAudio(audio, retryOrSkip);
      } catch {
        if (!cancelled) {
          setPlaying(false);
          retryOrSkip();
        }
      }
    };
    loadCurrent();
    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [current?.id, canFull, buildStreamUrl, reloadKey, retryOrSkip]); // eslint-disable-line react-hooks/exhaustive-deps

  const playTrack = useCallback((track, list, options = {}) => {
    retryCountRef.current = 0;
    shouldResumeRef.current = true;
    preparedNextRef.current = null;
    preparingNextRef.current = null;
    directTransitionRef.current = null;
    if (list && list.length) {
      const i = list.findIndex((t) => t.id === track.id);
      setQueue(list);
      setIndex(i >= 0 ? i : 0);
    } else {
      setQueue([track]);
      setIndex(0);
    }
    if (options.shuffleSource !== undefined) {
      setShuffleSource(options.shuffleSource || "");
    }
  }, []);

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) {
      shouldResumeRef.current = true;
      playAudio(a, retryOrSkip);
    } else {
      shouldResumeRef.current = false;
      a.pause();
    }
  }, [current, retryOrSkip]);

  const seek = useCallback((t) => {
    if (audioRef.current) {
      const max = previewMode ? PREVIEW_SEC : audioRef.current.duration || duration || 0;
      const nextTime = Math.max(0, Math.min(Number.isFinite(t) ? t : 0, max));
      audioRef.current.currentTime = nextTime;
      setProgress(nextTime);
    }
  }, [duration, previewMode]);

  useEffect(() => {
    const resume = () => {
      const audio = audioRef.current;
      if (!audio || !current || !shouldResumeRef.current || !audio.paused) return;
      playAudio(audio, retryOrSkip);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") resume();
    };
    window.addEventListener("online", resume);
    window.addEventListener("pageshow", resume);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("online", resume);
      window.removeEventListener("pageshow", resume);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [current, retryOrSkip]);

  useEffect(() => {
    if (!("mediaSession" in navigator) || !current) return undefined;
    const mediaSession = navigator.mediaSession;
    if ("MediaMetadata" in window) {
      mediaSession.metadata = new window.MediaMetadata({
        title: current.title || "StockMusic.ro",
        artist: current.artist || "stockmusicro",
        album: current.genre || "StockMusic.ro",
        artwork: [{ src: coverFor(current), sizes: "512x512" }],
      });
    }
    const handlers = {
      play: () => {
        shouldResumeRef.current = true;
        playAudio(audioRef.current, retryOrSkip);
      },
      pause: () => {
        shouldResumeRef.current = false;
        audioRef.current?.pause();
      },
      previoustrack: prev,
      nexttrack: next,
      seekbackward: (details) => seek((audioRef.current?.currentTime || 0) - (details.seekOffset || 10)),
      seekforward: (details) => seek((audioRef.current?.currentTime || 0) + (details.seekOffset || 10)),
      seekto: (details) => seek(details.seekTime),
    };
    Object.entries(handlers).forEach(([action, handler]) => {
      try {
        mediaSession.setActionHandler(action, handler);
      } catch {}
    });
    return () => {
      Object.keys(handlers).forEach((action) => {
        try {
          mediaSession.setActionHandler(action, null);
        } catch {}
      });
    };
  }, [current, next, prev, retryOrSkip, seek]);

  useEffect(() => {
    if (!("mediaSession" in navigator)) return;
    navigator.mediaSession.playbackState = playing ? "playing" : "paused";
  }, [playing]);

  return (
    <PlayerCtx.Provider
      value={{
        current, playing, progress, duration, volume, muted,
        fullscreen, previewMode, previewLimit: PREVIEW_SEC, shuffle, shuffleSource,
        setVolume, setMuted, setFullscreen, setShuffle, setShuffleSource,
        playTrack, toggle, next, prev, seek,
      }}
    >
      {children}
      <audio ref={audioRef} preload="auto" playsInline aria-hidden="true" />
    </PlayerCtx.Provider>
  );
}

export const usePlayer = () => useContext(PlayerCtx);
